// Arena colours for one frame (owner override; art-assets §2): the render style ('classic' by
// default, or 'sketch'), light or dark, and the optional theme.json overrides from the manifest.
//
// - Both styles take `palette` (pellet colours), `virus`, `spawner` and `plain` from theme.json.
//   Without a `palette`, classic pellets use the generated agar-like colours (visuals §2).
// - Only 'sketch' takes `paperLight`/`paperDark`, `gridLight`/`gridDark` and `ink`: those keys
//   name the paper-and-ink look. 'classic' keeps its agar-like near-white (or dark) arena.
//
// `css(rgb)` caches colour strings so the hot draw loops never format colours.

import {
  ARENA_COLORS,
  type ArenaColors,
  hexToRgb,
  mixRgb,
  PLAYER_PALETTE,
  type RenderStyle,
  type Rgb,
  rgbToHex,
  SPECIAL_COLORS,
} from '@ballio/shared';
import type { ManifestTheme } from '../assets/manifest.js';

export interface ArenaTheme {
  readonly style: RenderStyle;
  readonly dark: boolean;
  /** Text cache theme key, e.g. `classic-light` (render/text-cache.ts). */
  readonly key: string;
  readonly colors: Readonly<ArenaColors>;
  /**
   * Pellet palette (mechanics §5.1): theme.json `palette`; sketch's default 24 colours; or null
   * in the classic style = agar-like generated colours from the pellet id (visuals §2).
   */
  readonly palette: readonly Rgb[] | null;
  readonly virus: Rgb;
  readonly spawner: Rgb;
  /** "Plain colors" grey (ui-flow §4.5). */
  readonly plain: Rgb;
  /** Ink for sketch outlines and doodles. */
  readonly ink: Rgb;
}

/** `classic-light`, `sketch-dark`, … */
export function textThemeKey(style: RenderStyle, dark: boolean): string {
  return `${style}-${dark ? 'dark' : 'light'}`;
}

function hex(s: string | undefined): Rgb | null {
  if (typeof s !== 'string') return null;
  try {
    return hexToRgb(s);
  } catch {
    return null;
  }
}

function paletteOf(list: readonly string[] | undefined): readonly Rgb[] | null {
  if (!list) return null;
  const out: Rgb[] = [];
  for (const s of list) {
    const c = hex(s);
    if (c !== null) out.push(c);
  }
  return out.length >= 1 ? out : null;
}

/** The arena theme for a style, light/dark, and the manifest's theme.json overrides. */
export function resolveTheme(
  style: RenderStyle,
  dark: boolean,
  overrides?: ManifestTheme | null,
): ArenaTheme {
  const base = ARENA_COLORS[style][dark ? 'dark' : 'light'];
  const colors: ArenaColors = { ...base };
  const t = overrides ?? {};
  const ink = (style === 'sketch' ? hex(t.ink) : null) ?? SPECIAL_COLORS.ink;
  if (style === 'sketch') {
    const paper = hex(dark ? t.paperDark : t.paperLight);
    const grid = hex(dark ? t.gridDark : t.gridLight);
    const inkOverride = hex(t.ink);
    if (paper !== null) colors.background = paper;
    if (grid !== null) {
      colors.grid = grid;
      colors.gridMajor = grid;
      if (dark) colors.border = grid;
    }
    if (!dark && inkOverride !== null) {
      colors.border = inkOverride;
      colors.nameOutline = inkOverride;
    }
    if (paper !== null || (!dark && inkOverride !== null)) {
      // Outside the map: 6 % ink over the paper (architecture §9.2); darker chalk in dark mode.
      colors.outside = dark
        ? mixRgb(colors.background, 0x000000, 0.25)
        : mixRgb(colors.background, ink, 0.06);
    }
  }
  return Object.freeze({
    style,
    dark,
    key: textThemeKey(style, dark),
    colors: Object.freeze(colors),
    palette: paletteOf(t.palette) ?? (style === 'sketch' ? PLAYER_PALETTE : null),
    virus: hex(t.virus) ?? SPECIAL_COLORS.virus,
    spawner: hex(t.spawner) ?? SPECIAL_COLORS.spawner,
    plain: hex(t.plain) ?? SPECIAL_COLORS.plain,
    ink,
  });
}

const CSS_CACHE_MAX = 4096;
const cssCache = new Map<number, string>();

/** `#rrggbb` for a colour, cached (canvas fill/stroke styles). */
export function css(c: Rgb): string {
  let s = cssCache.get(c);
  if (s === undefined) {
    if (cssCache.size >= CSS_CACHE_MAX) cssCache.clear();
    s = rgbToHex(c);
    cssCache.set(c, s);
  }
  return s;
}

/** `rgb(r g b / a)` (not cached; for the few translucent fills). */
export function cssAlpha(c: Rgb, alpha: number): string {
  const r = (c >>> 16) & 0xff;
  const g = (c >>> 8) & 0xff;
  const b = c & 0xff;
  return `rgb(${r} ${g} ${b} / ${alpha})`;
}
